import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

/**
 * Standardized underline-tab style per design spec section 6.
 * - No pills, no button groups, no shadow.
 * - Inactive: 14px, muted text.
 * - Active: 14px, full white + 2px bottom border in primary.
 * - 24px gap between tabs.
 * - 1px solid bottom border at 10% opacity behind the tabs.
 * - 20px bottom margin before content.
 *
 * Existing className overrides still pass through, so consumers that
 * explicitly need a different look can opt out per-instance.
 */
const TabsList = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "flex items-center justify-start gap-6 border-b border-white/10 text-muted-foreground mb-5",
      className
    )}
    {...props} />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap text-sm font-normal transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:text-foreground data-[state=active]:text-foreground data-[state=active]:font-medium relative -mb-px border-b-2 border-transparent data-[state=active]:border-primary pb-2 pt-1",
      className
    )}
    {...props} />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props} />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
